import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUturnLeftIcon,
  ChevronRightIcon,
  FolderIcon,
  FolderPlusIcon,
  HomeIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { docConvertAPI } from '../../api/docConvertApi';
import { translateDepartments } from './departmentLabels';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';

const formatDate = (value) => {
  if (!value) return '—';
  const [y, m, d] = String(value).split('-');
  return d ? `${d}.${m}.${y}` : value;
};

const getStatusBadgeClass = (value) => {
  switch (value) {
    case 'Açık':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'Kapalı':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    case 'Dönüş yapıldı':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};

export default function NoteFoldersPage() {
  const navigate = useNavigate();
  const [currentId, setCurrentId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [notes, setNotes] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Klasör oluştur / yeniden adlandır modalı
  const [modalMode, setModalMode] = useState(null); // 'create' | 'rename'
  const [modalName, setModalName] = useState('');
  const [modalBusy, setModalBusy] = useState(false);

  const load = useCallback(async (parentId) => {
    setLoading(true);
    setError(null);
    try {
      const folderResp = await docConvertAPI.getFolders(parentId || undefined);
      setFolders(folderResp?.data || []);
      setBreadcrumb(folderResp?.breadcrumb || []);
      if (parentId) {
        const noteResp = await docConvertAPI.getDailyNotes({ folder: parentId, page_size: 0 });
        setNotes(noteResp?.data || []);
      } else {
        setNotes([]);
      }
      setCurrentId(parentId ?? null);
      setSelectedFolderId(null);
    } catch (err) {
      setError(err.message || 'Klasörler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(null);
  }, [load]);

  const openFolder = (id) => load(id);

  const goUp = () => {
    if (breadcrumb.length <= 1) {
      load(null);
    } else {
      load(breadcrumb[breadcrumb.length - 2].id);
    }
  };

  const openCreate = () => {
    setModalMode('create');
    setModalName('');
  };

  const openRename = () => {
    const folder = folders.find((f) => f.id === selectedFolderId);
    if (!folder) return;
    setModalMode('rename');
    setModalName(folder.name);
  };

  const submitModal = async () => {
    const name = modalName.trim();
    if (!name || modalBusy) return;
    setModalBusy(true);
    setError(null);
    try {
      if (modalMode === 'create') {
        await docConvertAPI.createFolder(name, currentId);
      } else if (modalMode === 'rename' && selectedFolderId) {
        await docConvertAPI.renameFolder(selectedFolderId, name);
      }
      setModalMode(null);
      await load(currentId);
    } catch (err) {
      setError(err.message || 'İşlem başarısız.');
    } finally {
      setModalBusy(false);
    }
  };

  const deleteFolder = async () => {
    const folder = folders.find((f) => f.id === selectedFolderId);
    if (!folder) return;
    if (
      !window.confirm(
        `"${folder.name}" klasörü silinsin mi?\n\nAlt klasörleri de silinir; içindeki notlar silinmez, klasörsüz hale gelir.`
      )
    ) {
      return;
    }
    try {
      await docConvertAPI.deleteFolder(folder.id);
      await load(currentId);
    } catch (err) {
      setError(err.message || 'Klasör silinemedi.');
    }
  };

  const removeNoteFromFolder = async (noteId) => {
    try {
      await docConvertAPI.moveNotesToFolder([noteId], null);
      await load(currentId);
    } catch (err) {
      setError(err.message || 'Not klasörden çıkarılamadı.');
    }
  };

  const openNote = (noteId) => {
    navigate(`/internal-audit/doc-convert/notes?note_id=${noteId}`);
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Klasörler</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Notlarınızı Windows tarzı klasörlerde düzenleyin. Notları "Günlük Notlar" ekranından seçip
          klasöre gönderebilirsiniz.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 flex justify-between items-start">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
            ×
          </button>
        </div>
      )}

      <ActionToolbar>
        <IconActionButton icon={FolderPlusIcon} onClick={openCreate} title="Yeni Klasör" />
        <IconActionButton
          icon={PencilSquareIcon}
          onClick={openRename}
          disabled={!selectedFolderId}
          title="Yeniden Adlandır"
        />
        <IconActionButton
          icon={TrashIcon}
          onClick={deleteFolder}
          disabled={!selectedFolderId}
          title="Klasörü Sil"
          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:text-gray-300"
        />
        <IconActionButton
          icon={ArrowUturnLeftIcon}
          onClick={goUp}
          disabled={currentId === null}
          title="Üst Klasöre Çık"
        />
      </ActionToolbar>

      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
        <button
          type="button"
          onClick={() => load(null)}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <HomeIcon className="h-4 w-4" /> Kök
        </button>
        {breadcrumb.map((crumb) => (
          <React.Fragment key={crumb.id}>
            <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" />
            <button
              type="button"
              onClick={() => load(crumb.id)}
              className="rounded px-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {loading && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300">
          Yükleniyor…
        </div>
      )}

      {/* Klasörler */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <h2 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
          Klasörler ({folders.length})
        </h2>
        {folders.length === 0 ? (
          <p className="text-sm text-gray-400">Bu konumda klasör yok. "Yeni Klasör" ile oluşturun.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {folders.map((folder) => {
              const selected = folder.id === selectedFolderId;
              return (
                <button
                  key={folder.id}
                  type="button"
                  onClick={() => setSelectedFolderId(folder.id)}
                  onDoubleClick={() => openFolder(folder.id)}
                  title="Aç (çift tıkla)"
                  className={`group flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-colors ${
                    selected
                      ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30'
                      : 'border-transparent hover:border-gray-200 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <FolderIcon className="h-12 w-12 text-amber-400" />
                  <span className="line-clamp-2 w-full break-words text-xs font-medium text-gray-800 dark:text-gray-100">
                    {folder.name}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {folder.subfolder_count > 0 && `${folder.subfolder_count} klasör`}
                    {folder.subfolder_count > 0 && folder.note_count > 0 && ' · '}
                    {folder.note_count > 0 && `${folder.note_count} not`}
                    {folder.subfolder_count === 0 && folder.note_count === 0 && 'boş'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Bu klasördeki notlar */}
      {currentId !== null && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="border-b border-gray-100 dark:border-gray-700 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Bu Klasördeki Notlar ({notes.length})
            </h2>
          </div>
          {notes.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400">
              Bu klasörde not yok. Notları "Günlük Notlar" ekranından seçip klasöre gönderin.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800/60 dark:text-gray-300">
                  <tr>
                    <th className="px-3 py-2">Sıra</th>
                    <th className="px-3 py-2">Tarih</th>
                    <th className="px-3 py-2">Başlık</th>
                    <th className="px-3 py-2">Not</th>
                    <th className="px-3 py-2">Departman</th>
                    <th className="px-3 py-2">Durum</th>
                    <th className="px-3 py-2 text-center">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {notes.map((note) => (
                    <tr
                      key={note.id}
                      onDoubleClick={() => openNote(note.id)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      title="Aç (çift tıkla)"
                    >
                      <td className="px-3 py-2 font-semibold text-gray-500">{note.sira_no ?? '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-500">{formatDate(note.not_tarihi)}</td>
                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-100">
                        {note.not_basligi || '—'}
                      </td>
                      <td className="max-w-[20rem] px-3 py-2 text-gray-600 dark:text-gray-300">
                        <span className="line-clamp-2 whitespace-pre-wrap">{note.not || '—'}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                        {translateDepartments(note.ilgili_departman) || '—'}
                      </td>
                      <td className="px-3 py-2">
                        {note.durum ? (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusBadgeClass(note.durum)}`}>
                            {note.durum}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNoteFromFolder(note.id);
                          }}
                          title="Klasörden çıkar"
                          className="inline-flex items-center gap-1 rounded p-1 text-gray-400 hover:bg-red-600 hover:text-white"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Oluştur / Yeniden adlandır modalı */}
      <ResponsiveModal
        isOpen={modalMode !== null}
        onClose={() => !modalBusy && setModalMode(null)}
        className="max-w-md w-[min(100%,calc(100vw-2rem))]"
      >
        <div className="px-4 pt-5 pb-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {modalMode === 'rename' ? 'Klasörü Yeniden Adlandır' : 'Yeni Klasör'}
            </h2>
            <button
              type="button"
              onClick={() => !modalBusy && setModalMode(null)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Klasör Adı</Label>
            <Input
              value={modalName}
              onChange={(e) => setModalName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitModal();
                }
              }}
              placeholder="Örn. Antalya Projeleri"
              autoFocus
            />
          </div>
          <div className="mt-6 flex items-center justify-end space-x-3">
            <Button type="button" variant="outline" onClick={() => setModalMode(null)} disabled={modalBusy}>
              İptal
            </Button>
            <Button
              type="button"
              onClick={submitModal}
              disabled={modalBusy || !modalName.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {modalBusy ? 'Kaydediliyor…' : modalMode === 'rename' ? 'Kaydet' : 'Oluştur'}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}
